import { OpDrawer } from "components/customAntd/DLS/OpDrawer/OpDrawer";
import { OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { OpTabs } from "components/customAntd/DLS/OpTabs/OpTabs";
import { MultiFormButtons } from "components/customAntd/MultiFormButtons";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "store/store";
import { Spin, notification } from "antd";
import { useConfirmModal } from "utils/customHooks/useConfirmModal"
import { DRAWER_WIDTH } from "constants/ui";
import { clearSelectedVisitorWorkflow } from "store/slices/visitorWorkflowsSlice";
import WorkflowContent from "./tabs/WorkflowContent";
import { LoadingOutlined } from '@ant-design/icons';
import { hasPermission } from "utils/utils";
import { logUserActivity } from "store/slices/userActivitySlice";
import { useTranslation } from "react-i18next";

interface AttendantSignOutDrawerProps {
    open: boolean;
    onClose: () => void;
}

const AttendantSignOutDrawer: React.FC<AttendantSignOutDrawerProps> = ({ open, onClose }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);

    const { selectedVisitorWorkflow, describeVisitorWorkflowLoading } = useSelector((state: RootState) => state.visitorWorkflows);

    const [activeKey, setActiveKey] = useState<string>('workflow');

    const [workflowForm] = OpForm.useForm();

    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasSignoutconfWrite = hasPermission(tokenScopeList, orgId, 'o', 'signoutconf:w');

    const confirmModal = useConfirmModal();
    const { t } = useTranslation();

    useEffect(() => {
        if (open) {
            setActiveKey('workflow');
            // Reset forms whenever the drawer opens to ensure they start fresh
            workflowForm.resetFields();
        }
    }, [open, workflowForm]);

    // Called when the save button is clicked
    const handleSubmitAllForms = async () => {
        try {
            const workflowValues = await workflowForm.validateFields();

            if (workflowValues) {
                if (workflowForm.isFieldsTouched()) {
                    await workflowForm.submit();
                    dispatch(logUserActivity({ orgId, userActivity: { userId: globalUserId!, activityId: "75", details: workflowForm.getFieldsValue().name || '' } }));

                    // Show notification only after all operations complete
                    notification.success({
                        message: t('notification.success'),
                        description: t('workflow.notification.updateSuccess'),
                        placement: 'bottomRight',
                    });
                }
                onClose();
            }
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('workflow.notification.saveError'),
                placement: 'bottomRight',
            });
        }
    };

    // Called when we close the drawer by clicking cancel button or touching the main page
    const finalOnClose = (e: React.MouseEvent | React.KeyboardEvent) => {
        if (workflowForm.isFieldsTouched()) {
            confirmModal({
                title: t('confirmDiscard.title'),
                content: t('confirmDiscard.message'),
                okText: t('confirmDiscard.ok'),
                cancelText: t('confirmDiscard.cancel'),
                onOk: () => {
                    workflowForm.resetFields();
                    dispatch(clearSelectedVisitorWorkflow());
                    onClose();
                },
            });
        } else {
            dispatch(clearSelectedVisitorWorkflow());
            onClose();
        }
    };

    const tabItems = [
        {
            key: 'workflow',
            label: t('workflow.kioskSignOut.tabs.workflow'),
            children: <WorkflowContent form={workflowForm} />,
        }
    ];

    return (
        <OpDrawer
            title={describeVisitorWorkflowLoading ? undefined : (selectedVisitorWorkflow ? `${selectedVisitorWorkflow.name}` : t('workflow.kioskSignOut.addTitle'))}
            width={DRAWER_WIDTH}
            open={open}
            onClose={finalOnClose}
            footer={
                !describeVisitorWorkflowLoading && hasSignoutconfWrite && (
                    <MultiFormButtons
                        forms={[workflowForm]}
                        onSubmitClick={handleSubmitAllForms}
                        isSubmitButtonLoading={describeVisitorWorkflowLoading}
                    />
                )
            }
        >
            {describeVisitorWorkflowLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                </div>
            ) : (
                <OpTabs
                    activeKey={activeKey}
                    onChange={(newActiveKey: string) => {
                        setActiveKey(newActiveKey);
                    }}
                    items={tabItems}
                />
            )}
        </OpDrawer>
    );
};

export default AttendantSignOutDrawer;